'use strict';

import { Entity } from '../Entity';
import { User } from '../User/User';
import { AutomationAction } from './AutomationAction';
import { Utilities } from '../../Utilities/Utilities';
import { AutomationActionCollection } from '../../Collections/Automation/AutomationActionCollection';

export const AUTOMATION_SHELL_TYPE = {
  STARTER: 'starter',
  BLANK: 'blank',
};
export type AUTOMATION_SHELL_TYPE =
  (typeof AUTOMATION_SHELL_TYPE)[keyof typeof AUTOMATION_SHELL_TYPE];

// Trgger Data: [field]
// TODO: Add component refrence and wether this field can be "Track all" or if it's redundant
export const AUTOMATION_TRIGGER_DATA_FIELD_TYPE = {
  IND_STATUS: 'indStatus',
  USER_FLAG: 'userFlag',
  USER_SOURCE: 'userSource',
  PLAN_TYPE: 'policyType',
  USER_LEAD_STATUS: 'leadStatus',
  IND_POLICY_STATUS: 'policyStatus',
  IND_POLICY_WRITTEN_AS: 'policyWrittenAs',
  ACCUMULATION_VALUE: 'accumulationValue',
  IND_PREFERRED_LANGUAGE: 'indPrefLang',
  IND_GENDER: 'indGender',
  IND_AGE: 'indAge',
  IND_BIRTHDAY_UPCOMING: 'birthdayUpcoming',
  IND_POLICY_COMPANY: 'policyCompany',
} as const;

export type AUTOMATION_TRIGGER_DATA_FIELD_TYPE =
  (typeof AUTOMATION_TRIGGER_DATA_FIELD_TYPE)[keyof typeof AUTOMATION_TRIGGER_DATA_FIELD_TYPE];

export interface AutomationActionTriggerData {
  field: AUTOMATION_TRIGGER_DATA_FIELD_TYPE;
  allValues?: boolean;
  values?: any[];
  required?: boolean;
}

export const AUTOMATION_ACTION_TRIGGER_TYPE = {
  INSERT: 'insert',
  UPDATE: 'update',
  UPSERT: 'upsert',

  // Special
  BIRTHDAY: 'birthday',
  UPCOMING_BIRTHDAY: 'birthday_upcoming',
};

export type AUTOMATION_ACTION_TRIGGER_TYPE =
  (typeof AUTOMATION_ACTION_TRIGGER_TYPE)[keyof typeof AUTOMATION_ACTION_TRIGGER_TYPE];

// Trigger Data
export const AUTOMATION_TRIGGER_COMBINATION = {
  USER_LEAD_INSERT: 'user_lead_insert',
  USER_LEAD_STATUS_INSERT: 'user_lead_status_insert',
  USER_LEAD_STATUS_UPDATE: 'user_lead_status_update',
  USER_LEAD_STATUS_UPSERT: 'user_lead_status_upsert',
  IND_POLICY_STATUS_INSERT: 'ind_policy_status_insert',
  IND_POLICY_STATUS_UPDATE: 'ind_policy_status_update',
  IND_POLICY_STATUS_UPSERT: 'ind_policy_status_upsert',
  IND_POLICY_PREMIUM_INSERT: 'ind_policy_premium_insert',
  IND_BIRTHDAY: 'ind_birthday',
  IND_BIRTHDAY_UPCOMING: 'ind_birthday_upcoming',
  IND_STATUS_INSERT: 'ind_status_insert',
  IND_STATUS_UPDATE: 'ind_status_update',
} as const;

export type AUTOMATION_TRIGGER_COMBINATION =
  (typeof AUTOMATION_TRIGGER_COMBINATION)[keyof typeof AUTOMATION_TRIGGER_COMBINATION];

export interface AutomationPayload {
  name?: string;
  canOverride?: boolean;
  isActive?: boolean;
  isShared?: boolean;
  parentAutomation?: number;
  recipe?: AUTOMATION_TRIGGER_COMBINATION;
  triggerEntity?: string;
  triggerType?: AUTOMATION_ACTION_TRIGGER_TYPE;
  triggerData?: AutomationActionTriggerData[];
}

export class Automation extends Entity {
  protected id;
  protected name;
  protected dateTimeCreated;
  protected canOverride;
  protected isActive = false;
  protected isShared = false;
  protected isDeleted = false;
  protected automationActions = new AutomationActionCollection();
  protected user;
  protected parentAutomation;
  protected recipe: AUTOMATION_TRIGGER_COMBINATION;
  protected triggerEntity: string;
  protected triggerType: AUTOMATION_ACTION_TRIGGER_TYPE;
  protected triggerData: AutomationActionTriggerData[];

  constructor() {
    super();
  }

  public hydrate(data: any) {
    this.id = data.id;
    this.name = data.name;
    this.dateTimeCreated = data.dateTimeCreated;
    this.canOverride = data.canOverride;
    this.isActive = data.isActive;
    this.isShared = data.isShared;
    this.isDeleted = data.isDeleted;
    this.automationActions = data.automationActions;
    this.parentAutomation = data.parentAutomation;
    this.triggerEntity = data.triggerEntity;
    this.triggerType = data.triggerType;
    this.triggerData = data.triggerData;
    this.recipe = data.recipe;

    if (data.user) {
      const user = new User();

      user.hydrate(data.user);

      this.user = user;
    }

    if (data.dateTimeCreated) {
      const dateTimeCreated = Utilities.dateTimeStringToDate(data.dateTimeCreated);
      this.dateTimeCreated = dateTimeCreated;
    }

    if (data.automationActions) {
      const automationActions = new AutomationActionCollection();

      data.automationActions.forEach((action) => {
        const automationAction = new AutomationAction();
        automationAction.hydrate(action);
        automationAction.setAutomation(this);
        automationActions.addItem(automationAction);
      });

      this.automationActions = automationActions;
    }

    if (data.parentAutomation) {
      const parentAutomation = new Automation();
      parentAutomation.hydrate(data.parentAutomation);

      this.parentAutomation = parentAutomation;
    }
  }

  public getName() {
    return this.name;
  }

  public setName(name: string) {
    this.name = name;
    return this;
  }

  public getDateTimeCreated() {
    return this.dateTimeCreated;
  }

  public getIsActive() {
    return this.isActive;
  }

  public setIsActive(isActive: boolean) {
    this.isActive = isActive;
    return this;
  }

  public getAutomationActions() {
    return this.automationActions;
  }

  public setAutomationActions(automationActions) {
    this.automationActions;
  }

  public addAutomationAction(automationAction) {
    this.automationActions.addItem(automationAction);
  }

  public getUser() {
    return this.user;
  }

  public getCanOverride() {
    return this.canOverride;
  }

  public setCanOverride(canOverride: boolean) {
    this.canOverride = canOverride;
    return this;
  }

  public getIsShared() {
    return this.isShared;
  }

  public setIsShared(isShared: boolean) {
    this.isShared = isShared;
    return this;
  }

  public getIsDeleted() {
    return this.isDeleted;
  }

  public setIsDeleted(isDeleted: boolean) {
    this.isDeleted = isDeleted;
    return this;
  }

  public getParentAutomation() {
    return this.parentAutomation;
  }

  public getTriggerEntity(): string {
    return this.triggerEntity;
  }

  public setTriggerEntity(triggerEntity: string) {
    this.triggerEntity = triggerEntity;
    return this;
  }

  public getTriggerType(): AUTOMATION_ACTION_TRIGGER_TYPE {
    return this.triggerType;
  }

  public setTriggerType(triggerType: AUTOMATION_ACTION_TRIGGER_TYPE) {
    this.triggerType = triggerType;
    return this;
  }

  public getTriggerData() {
    return this.triggerData;
  }

  public setTriggerData(triggerData: any) {
    this.triggerData = triggerData;
    return this;
  }

  public getRecipe(): AUTOMATION_TRIGGER_COMBINATION {
    return this.recipe;
  }

  public setRecipe(recipe: AUTOMATION_TRIGGER_COMBINATION) {
    this.recipe = recipe;
    return this;
  }
}
